import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import {
    QpxAvatar,
    QpxButton,
    QpxIcon,
    QpxPill,
    QpxTooltip,
} from '@quipex/shared/ui';
import { resolveColorContext, type ContextFamily } from '@quipex/shared/utils';
import { createSuggestionPages } from '../create-suggestion-pages';
import type {
    QuippySuggestedQuestion,
    QuippySuggestionCategory,
} from '../models';
import { CATEGORY_ICONS } from '../personas/models';
import { SuggestionPagination } from '../suggestion-pagination/suggestion-pagination';

import { CATEGORY_COLOR_CONTEXTS } from './category-colors';
import { SuggestionCategoryFilter } from './suggestion-category-filter/suggestion-category-filter';
import { SuggestionRowFit } from './suggestion-row-fit';

const ALL_CATEGORIES = 'All' as const;
type CategoryFilter = typeof ALL_CATEGORIES | QuippySuggestionCategory;

const CATEGORY_OPTIONS = [
    'Understand it',
    'Keep it running',
    'Manage risk',
    'Meet obligations',
    'Find evidence',
    'Coordinate people',
    'Improve performance',
    'Assess value & use',
    'Plan change',
] as const satisfies readonly QuippySuggestionCategory[];

@Component({
    selector: 'qpx-quippy-suggested-questions',
    imports: [
        MatButtonModule,
        QpxTooltip,
        SuggestionPagination,
        NgIcon,
        QpxAvatar,
        QpxButton,
        QpxIcon,
        QpxPill,
        SuggestionRowFit,
        SuggestionCategoryFilter,
    ],
    templateUrl: './quippy-suggested-questions.html',
    styleUrl: './quippy-suggested-questions.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'region',
        '[attr.aria-label]': 'ariaLabel()',
        '[class.without-leading-avatar]': '!showLeadingAvatar()',
    },
})
export class QuippySuggestedQuestions {
    protected readonly isPhone = injectViewportBreakpoints().isPhone;

    //#region Inputs
    public readonly headingId = input('quippy-suggested-questions-title');
    public readonly heading = input('Suggested questions');
    public readonly ariaLabel = input('Suggested questions');
    public readonly questions =
        input.required<readonly QuippySuggestedQuestion[]>();
    public readonly appearance = input<'elevated' | 'outlined'>('elevated');
    public readonly pageSize = input(5);
    public readonly categoryFirst = input(false);
    public readonly initialCategory = input<QuippySuggestionCategory | null>(
        null
    );
    public readonly showCategories = input(true);
    public readonly allowCategoryFilter = input(true);
    public readonly compactRows = input(false);
    public readonly resetKey = input<string | null>(null);
    public readonly showLeadingAvatar = input(true);
    //#endregion

    //#region Outputs
    public readonly questionSelected = output<QuippySuggestedQuestion>();
    //#endregion

    //#region Filter state
    protected readonly categoryOptions = computed<readonly CategoryFilter[]>(
        () => {
            const available = new Set(
                this.questions().map(({ category }) => category)
            );
            return [
                ALL_CATEGORIES,
                ...CATEGORY_OPTIONS.filter((category) =>
                    available.has(category)
                ),
            ];
        }
    );
    protected readonly categoryIcons = CATEGORY_ICONS;
    protected readonly availableCategories = computed(() =>
        this.categoryOptions().filter(
            (category): category is QuippySuggestionCategory =>
                category !== ALL_CATEGORIES
        )
    );
    protected readonly selectedCategory = linkedSignal({
        source: () => ({
            questions: this.questions(),
            resetKey: this.resetKey(),
            categoryFirst: this.categoryFirst(),
            initialCategory: this.initialCategory(),
        }),
        computation: ({
            categoryFirst,
            initialCategory,
            questions,
        }): CategoryFilter | null =>
            categoryFirst
                ? initialCategory &&
                  questions.some(({ category }) => category === initialCategory)
                    ? initialCategory
                    : null
                : ALL_CATEGORIES,
    });
    protected readonly showQuestions = computed(
        () => !this.categoryFirst() || this.selectedCategory() !== null
    );
    protected readonly filteredQuestions = computed(() => {
        const selectedCategory = this.selectedCategory();
        if (selectedCategory === null) return [];
        if (selectedCategory === ALL_CATEGORIES) return this.questions();

        return this.questions().filter(
            ({ category }) => category === selectedCategory
        );
    });
    protected readonly showCategoryFilter = computed(
        () =>
            this.showCategories() &&
            this.allowCategoryFilter() &&
            !this.categoryFirst() &&
            this.questions().length > Math.max(1, Math.floor(this.pageSize()))
    );
    //#endregion

    //#region State
    protected readonly pages = createSuggestionPages(
        this.filteredQuestions,
        this.pageSize
    );
    //#endregion

    //#region Template methods
    protected isCategoryIconOnly(category: QuippySuggestionCategory): boolean {
        return (
            this.isPhone() &&
            this.selectedCategory() !== null &&
            this.selectedCategory() !== category
        );
    }

    protected getCategoryButtonAppearance(): 'elevated' | 'filled' {
        return this.appearance() === 'elevated' ? 'elevated' : 'filled';
    }

    protected getCategoryButtonContext(
        category: QuippySuggestionCategory
    ): ContextFamily {
        return this.getCategoryColorContext(category);
    }

    protected selectQuestion(question: QuippySuggestedQuestion): void {
        this.questionSelected.emit(question);
    }

    protected selectCategory(category: CategoryFilter): void {
        this.selectedCategory.set(category);
    }

    protected getCategoryColorContext(
        category: QuippySuggestionCategory
    ): ContextFamily {
        return CATEGORY_COLOR_CONTEXTS[category];
    }

    protected getCategoryOutline(category: QuippySuggestionCategory): string {
        return `var(${resolveColorContext(this.getCategoryColorContext(category), 'muted').decorative.variable})`;
    }

    //#endregion
}
